import React, { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { AuthModalProvider } from './contexts/AuthModalContext';
import { apiClient } from './services/api';
import TopBar from './components/navigation/TopBar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import DesktopShell from './layouts/DesktopShell';
import LoginFlowModal from './components/auth/LoginFlowModal';
import AgeGateOverlay from './components/AgeGateOverlay';
import RequireAuthGate from './components/guards/RequireAuthGate';
import ErrorBoundary from './components/ErrorBoundary';
import { updateLastNonLoginPath } from './utils/authNavigation';
import { useTracking } from './hooks/useTracking';
import { getSubscriptionPlans } from './services/credits';
import { getUserAssets } from './services/assets';
import { usePostHog } from 'posthog-js/react';
import { OAUTH_RETURN_KEY } from './utils/constants';

// Eagerly loaded — needed on every page or during auth flow
import HomePage from './pages/HomePage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import LoginPage from './pages/LoginPage';
// Suspense fallbacks must be eager (a lazy fallback that suspends itself causes React #426)
import Lander1Skeleton from './components/skeletons/Lander1Skeleton';
import Lander3Skeleton from './components/skeletons/Lander3Skeleton';

// Lazy loaded — only downloaded when user navigates to these routes
// Landing pages
const Lander1Page = lazy(() => import('./pages/Lander1Page'));
const Lander3Page = lazy(() => import('./pages/Lander3Page'));
const AdPage2 = lazy(() => import('./pages/AdPage2'));
const I2VPage = lazy(() => import('./pages/I2VPage'));
// Core pages
const Video = lazy(() => import('./pages/Video'));
const Image = lazy(() => import('./pages/Image'));
const MyVideos = lazy(() => import('./pages/MyVideos'));
const PricingPage = lazy(() => import('./components/PricingPage'));
const ImageEditingPage = lazy(() => import('./pages/ImageEditingPage'));
const TemplateToVideoPage = lazy(() => import('./pages/TemplateToVideoPage'));
const TemplatesPage = lazy(() => import('./pages/TemplatesPage'));
// Payments & upgrade (imports Stripe SDK — big win from lazy loading)
const UpgradePage = lazy(() => import('./pages/UpgradePage'));
const UpgradeRouter = lazy(() => import('./components/UpgradeRouter'));
const CheckoutEmbeddedPage = lazy(() => import('./pages/CheckoutEmbeddedPage'));
const CheckoutReturnPage = lazy(() => import('./pages/CheckoutReturnPage'));
const CreditsPage = lazy(() => import('./pages/CreditsPage'));
const CreditsSuccessPage = lazy(() => import('./pages/CreditsSuccessPage'));
// Static pages
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const MobilePrivacyPolicyPage = lazy(() => import('./pages/MobilePrivacyPolicyPage'));
const MobileTermsOfServicePage = lazy(() => import('./pages/MobileTermsOfServicePage'));
const RefundPolicyPage = lazy(() => import('./pages/RefundPolicyPage'));
const DeleteAccountPage = lazy(() => import('./pages/DeleteAccountPage'));
const GuidesIndexPage = lazy(() => import('./pages/GuidesIndexPage'));
const GuidePage = lazy(() => import('./pages/GuidePage'));

// Tracks whether the viewport is at/above the `lg` breakpoint (1024px).
// Re-renders when the viewport crosses the breakpoint so we mount exactly
// one layout (mobile OR desktop) instead of hiding one with CSS.
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    () => window.matchMedia('(min-width: 1024px)').matches
  );

  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1024px)');
    const handleChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  return isDesktop;
}

function AppContent() {
  const { loading: isAuthLoading, getAccessToken, isAuthenticated, session } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { track } = useTracking();
  const posthog = usePostHog();
  const isDesktop = useIsDesktop();

  // Set up API client to use Supabase tokens
  useEffect(() => {
    apiClient.setAccessTokenGetter(getAccessToken);
  }, [getAccessToken]);

  // Prefetch commonly accessed data on app load to warm cache
  useEffect(() => {
    // Prefetch subscription plans (used on /upgrade page)
    getSubscriptionPlans().catch(() => {
      // Silent fail - cache warming is best-effort
    });

    // If user is authenticated, prefetch their videos
    if (isAuthenticated) {
      getUserAssets('video').catch(() => {
        // Silent fail - cache warming is best-effort
      });
    }
  }, [isAuthenticated]);

  // Force dark mode
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  // OAuth hash on homepage - redirect to /auth/callback if we're expecting it
  // This handles edge cases where OAuth redirects to / instead of /auth/callback
  useEffect(() => {
    if (location.pathname !== '/') return;

    const hash = window.location.hash;
    const hasOAuthTokens = hash.includes('access_token=') || hash.includes('error=');
    const oauthPending = !!localStorage.getItem(OAUTH_RETURN_KEY);

    if (hasOAuthTokens && oauthPending) {
      // OAuth pending - redirect to auth callback page
      console.log('[App] OAuth hash on homepage, OAuth pending - redirecting to /auth/callback');
      navigate('/auth/callback' + window.location.search + hash, { replace: true });
      return;
    }

    // If we have tokens but NOT expecting, this is stale - clean up
    if (hasOAuthTokens && !oauthPending) {
      console.warn('[App] Stale OAuth tokens on homepage - cleaning up');
      window.history.replaceState(null, '', '/');
    }
  }, [location.pathname, navigate]);

  // Track last non-login path in sessionStorage
  useEffect(() => {
    updateLastNonLoginPath(location.pathname);
  }, [location.pathname]);

  // Save current path when page becomes hidden (mobile browser backgrounding)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page hidden - save current path (except auth/login pages)
        const currentPath = location.pathname;
        if (currentPath !== '/auth/callback' && currentPath !== '/login') {
          sessionStorage.setItem('ga:lastPath', currentPath);
          // Also save timestamp to detect stale state
          sessionStorage.setItem('ga:lastPathTimestamp', Date.now().toString());
        }
      } else {
        // Page visible - restore path if we're on wrong page
        const savedPath = sessionStorage.getItem('ga:lastPath');
        const savedTimestamp = sessionStorage.getItem('ga:lastPathTimestamp');
        
        if (savedPath && savedTimestamp) {
          const age = Date.now() - parseInt(savedTimestamp);
          const maxAge = 30 * 60 * 1000; // 30 minutes (for Meta ad users who may be distracted)
          
          // Only restore if:
          // 1. Saved path is different from current
          // 2. Not on auth/login pages
          // 3. Saved state is recent (< 30 minutes)
          // 4. Not on /lander-1, /lander-3, or /i2v (let those pages handle their own restoration)
          if (age < maxAge &&
              savedPath !== location.pathname &&
              location.pathname !== '/auth/callback' &&
              location.pathname !== '/login' &&
              location.pathname !== '/lander-1' &&
              location.pathname !== '/lander-3' &&
              location.pathname !== '/ad-page-2' &&
              location.pathname !== '/i2v') {
            
            console.log('[App] Restoring navigation state:', savedPath);
            navigate(savedPath, { replace: true });
          }
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [location.pathname, navigate]);

  // Track pageviews - global deduplication in useTracking will prevent duplicates
  // Use ref to avoid dependency on track function
  const trackRef = useRef(track);
  trackRef.current = track;
  
  useEffect(() => {
    // Skip tracking for auth callback page (handled separately)
    if (location.pathname !== '/auth/callback') {
      trackRef.current('pageview', {
        page_path: location.pathname,
        page_title: document.title,
        page_url: window.location.href,
      });
    }
  }, [location.pathname]); // Remove track from dependencies

  // UNIFIED GUARD: Block stale OAuth callbacks and redirect away from callback page
  // Uses localStorage key as single source of truth (REVENUE PROTECTION)
  const isAuthCallbackPage = location.pathname === '/auth/callback';
  useEffect(() => {
    const urlHasAccessToken = window.location.hash.includes('access_token=');
    const currentPath = location.pathname;

    // Skip if auth is still loading
    if (isAuthLoading) return;

    // Skip if no OAuth indicators and not on callback page
    if (!urlHasAccessToken && !isAuthCallbackPage) return;

    // Allow if either:
    //   (a) localStorage flag set (normal same-origin OAuth), OR
    //   (b) session.user.last_sign_in_at is fresh (< 120s).
    // (b) is required because Android Custom Tab → WebView OAuth handoff
    // loses OAUTH_RETURN_KEY across browser contexts; localStorage absence
    // there is a false negative. last_sign_in_at lives on the session, not
    // in storage, so it survives the handoff. AuthCallbackPage has the
    // same gate; this is its earlier sibling in the App tree.
    const oauthPending = !!localStorage.getItem(OAUTH_RETURN_KEY);
    const lastSignInMs = session?.user?.last_sign_in_at
      ? Date.parse(session.user.last_sign_in_at)
      : 0;
    const isFreshSignIn = lastSignInMs > 0 && Date.now() - lastSignInMs < 120_000;

    if (oauthPending || isFreshSignIn) {
      console.log('[App] OAuth callback allowed', { oauthPending, isFreshSignIn });
      return;
    }

    // NOT expecting a callback - this is stale
    const isCriticalPage = currentPath === '/upgrade' ||
                          currentPath === '/credits' ||
                          currentPath.includes('checkout') ||
                          currentPath === '/lander-1' ||
                          currentPath === '/lander-3' ||
                          currentPath === '/i2v';

    console.warn('[App] BLOCKED stale OAuth', {
      currentPath,
      isCriticalPage,
      hasAccessToken: urlHasAccessToken,
      isCallbackPage: isAuthCallbackPage,
      isAuthenticated,
    });

    // Clean URL hash if it has tokens
    if (urlHasAccessToken) {
      const cleanUrl = window.location.origin + currentPath;
      window.history.replaceState(null, '', cleanUrl);
    }

    // Track for debugging
    posthog.capture('blocked_stale_oauth_callback', {
      blocking_layer: 'app_tsx',
      from_page: currentPath,
      is_critical_page: isCriticalPage,
      reason: 'oauth_not_pending',
      had_access_token: urlHasAccessToken,
      is_callback_page: isAuthCallbackPage,
    });

    // Do NOT redirect away from /auth/callback here. AuthCallbackPage owns
    // that page's lifecycle and runs the same last_sign_in_at freshness gate
    // — but it runs after Supabase has fully processed the URL, avoiding the
    // React state-race where this guard saw session=null mid-hydration and
    // false-positively blocked legitimate fresh OAuth callbacks (Android
    // Custom Tab → WebView and similar). The capture above remains, so we
    // keep visibility into how often this path is reached for analytics.
  }, [location.pathname, isAuthenticated, isAuthLoading, posthog, isAuthCallbackPage, session]);

  if (isAuthLoading) {
    return (
      <div className="h-screen bg-bg flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  // Auth callback page doesn't need TopBar/Footer
  // Note: isAuthCallbackPage is already defined above with the useEffect hook

  if (isAuthCallbackPage) {
    return (
      <div className="h-screen bg-bg">
        <ScrollToTop />
        <Routes>
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
        </Routes>
      </div>
    );
  }

  // Check if we're on landing/generation pages (hide all navigation)
  const isLander1 = location.pathname === '/lander-1';
  const isLander3 = location.pathname === '/lander-3';
  const isAdPage2 = location.pathname === '/ad-page-2';

  // Check if we're on the upgrade page (hide all navigation)
  const isUpgradePage = location.pathname === '/upgrade';

  // Special layout for lander-1: no navigation, full page
  if (isLander1) {
    return (
      <div className="min-h-screen bg-bg">
        <ScrollToTop />
        {/* Global auth modal - rendered once at app root */}
        <LoginFlowModal />
        <Suspense fallback={<Lander1Skeleton />}>
          <Routes>
            <Route path="/lander-1" element={<Lander1Page />} />
          </Routes>
        </Suspense>
      </div>
    );
  }

  // Special layout for lander-3: no navigation, full page (squeeze page)
  if (isLander3) {
    return (
      <div className="min-h-screen bg-bg">
        <ScrollToTop />
        <LoginFlowModal />
        <Suspense fallback={<Lander3Skeleton />}>
          <Routes>
            <Route path="/lander-3" element={<Lander3Page />} />
          </Routes>
        </Suspense>
      </div>
    );
  }

  // Special layout for ad-page-2: no navigation, full page (ported from c-lander-template)
  if (isAdPage2) {
    return (
      <div className="min-h-screen bg-bg">
        <ScrollToTop />
        <LoginFlowModal />
        <Suspense fallback={
          <div className="min-h-screen bg-bg flex items-center justify-center">
            <div className="animate-pulse text-white/60">Loading...</div>
          </div>
        }>
          <Routes>
            <Route path="/ad-page-2" element={<AdPage2 />} />
          </Routes>
        </Suspense>
      </div>
    );
  }

  // /i2v uses the standard layout (TopBar on mobile, DesktopShell on desktop)
  // just like the homepage — no special layout block needed.

  // Special layout for upgrade page: no navigation, full page
  if (isUpgradePage) {
    return (
      <div className="min-h-screen bg-bg">
        <ScrollToTop />
        {/* Global auth modal - rendered once at app root */}
        <LoginFlowModal />
        <Suspense fallback={
          <div className="min-h-screen bg-bg flex items-center justify-center">
            <div className="animate-pulse text-white/60">Loading...</div>
          </div>
        }>
          <Routes>
            <Route path="/upgrade" element={<ErrorBoundary><UpgradeRouter /></ErrorBoundary>} />
          </Routes>
        </Suspense>
      </div>
    );
  }

  // Single shared route table, deduped from the two former identical
  // mobile + desktop tables. Rendered once; the surrounding chrome differs
  // by breakpoint below.
  const routes = (
    <Suspense fallback={
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="animate-pulse text-white/60">Loading...</div>
      </div>
    }>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/video/:mode" element={<Video />} />
        <Route path="/image/:mode" element={<Image />} />
        <Route path="/image-to-video" element={<Navigate to="/i2v" replace />} />
        <Route path="/image-editing" element={<ImageEditingPage />} />
        <Route path="/template-to-video" element={<TemplateToVideoPage />} />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/lander-1" element={<Lander1Page />} />
        <Route path="/lander-3" element={<Lander3Page />} />
        <Route path="/i2v" element={<I2VPage />} />
        <Route path="/upgrade" element={<ErrorBoundary><UpgradeRouter /></ErrorBoundary>} />
        <Route path="/my-collection" element={<RequireAuthGate><MyVideos /></RequireAuthGate>} />
        <Route path="/assets" element={<Navigate to="/my-collection" replace />} />
        <Route path="/billing" element={<PricingPage />} />
        <Route path="/credits" element={<RequireAuthGate><CreditsPage /></RequireAuthGate>} />
        <Route path="/credits/success" element={<RequireAuthGate><CreditsSuccessPage /></RequireAuthGate>} />
        <Route path="/checkout" element={<CheckoutEmbeddedPage />} />
        <Route path="/return" element={<CheckoutReturnPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/privacy-policy" element={<MobilePrivacyPolicyPage />} />
        <Route path="/tos" element={<MobileTermsOfServicePage />} />
        <Route path="/refund-policy" element={<RefundPolicyPage />} />
        <Route path="/delete-account" element={<DeleteAccountPage />} />
        <Route path="/guides" element={<GuidesIndexPage />} />
        <Route path="/guides/:slug" element={<GuidePage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
      </Routes>
    </Suspense>
  );

  return (
    <div className="h-screen bg-bg dark:bg-bg flex flex-col overflow-x-hidden">
      <ScrollToTop />
      {/* Global auth modal - rendered once at app root */}
      <LoginFlowModal />
      {/* 18+ age gate — homepage only, shown to EU/EEA/UK visitors */}
      {location.pathname === '/' && <AgeGateOverlay />}
      <TopBar />

      {/* Render exactly one layout based on viewport width so page
          components mount once (not twice via CSS-hidden duplicate trees). */}
      {isDesktop ? (
        <div className="flex flex-col h-full">
          <DesktopShell>
            {routes}
          </DesktopShell>
        </div>
      ) : (
        <div className="flex flex-col h-full">
          <main className="flex-1 overflow-y-auto overflow-x-hidden">
            {routes}
            <Footer />
          </main>
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <AuthModalProvider>
      <AppContent />
    </AuthModalProvider>
  );
}

export default App;

